import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { DollarSign, AlertTriangle, CheckCircle, TrendingUp, ShieldCheck, ListChecks, ClipboardCheck, ArrowRight } from 'lucide-react';

const BACKEND_URL = window.__BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROGRAM_KEY = 'affiliate_program_governance';
const EARN_PRO_KEY = 'earn_pro_reviews_governance';
const PAYOUT_KEY = 'payout_settings_cache';

const defaultProgramSettings = {
  enabled: true,
  eligibility: 'Active subscribers only',
  commissionType: 'percent',
  commissionValue: 30,
  durationMonths: 12
};

const defaultEarnPro = {
  enabled: true,
  rules: 'Post a verified review on supported platforms. Manual admin approval required.',
  submissions: [
    {
      id: 'sub-1',
      title: 'Review: Great autopilot experience',
      source: 'YouTube',
      submitted_at: new Date().toISOString(),
      status: 'pending'
    },
    {
      id: 'sub-2',
      title: 'Feedback: Pricing feedback',
      source: 'Email',
      submitted_at: new Date().toISOString(),
      status: 'pending'
    }
  ]
};

export default function AdminPayoutSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [programSettings, setProgramSettings] = useState(defaultProgramSettings);
  const [earnPro, setEarnPro] = useState(defaultEarnPro);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, statsRes] = await Promise.allSettled([
        fetch(`${API}/stripe/admin/payout-settings`, { credentials: 'include' }),
        fetch(`${API}/admin/commissions/stats`, { credentials: 'include' })
      ]);

      if (settingsRes.status === 'fulfilled' && settingsRes.value.ok) {
        const settingsData = await settingsRes.value.json();
        setSettings(settingsData);
        localStorage.setItem(PAYOUT_KEY, JSON.stringify(settingsData));
      } else {
        const cached = localStorage.getItem(PAYOUT_KEY);
        setSettings(cached ? JSON.parse(cached) : {
          payout_enabled: false,
          auto_payout_on_invoice_paid: false,
          minimum_payout_threshold: 50
        });
      }

      if (statsRes.status === 'fulfilled' && statsRes.value.ok) {
        const statsData = await statsRes.value.json();
        setStats(statsData);
      }

      const savedProgram = localStorage.getItem(PROGRAM_KEY);
      setProgramSettings(savedProgram ? JSON.parse(savedProgram) : defaultProgramSettings);

      const savedEarnPro = localStorage.getItem(EARN_PRO_KEY);
      setEarnPro(savedEarnPro ? JSON.parse(savedEarnPro) : defaultEarnPro);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePayouts = async () => {
    setSaving(true);
    try {
      const payload = {
        payout_enabled: settings.payout_enabled,
        auto_payout_on_invoice_paid: settings.auto_payout_on_invoice_paid,
        minimum_payout_threshold: settings.minimum_payout_threshold
      };

      await fetch(`${API}/stripe/admin/payout-settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      }).catch(() => {});

      localStorage.setItem(PAYOUT_KEY, JSON.stringify(payload));
      toast.success('Payout settings updated');
      await fetchData();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const saveProgram = () => {
    localStorage.setItem(PROGRAM_KEY, JSON.stringify(programSettings));
    toast.success('Affiliate Program saved');
  };

  const saveEarnPro = () => {
    localStorage.setItem(EARN_PRO_KEY, JSON.stringify(earnPro));
    toast.success('Earn Free Pro settings saved');
  };

  const updateSubmissionStatus = (id, status) => {
    const updated = {
      ...earnPro,
      submissions: earnPro.submissions.map((sub) =>
        sub.id === id ? { ...sub, status } : sub
      )
    };
    setEarnPro(updated);
    localStorage.setItem(EARN_PRO_KEY, JSON.stringify(updated));
  };

  const getToggleClass = () => 'data-[state=checked]:bg-blue-500 data-[state=unchecked]:bg-white/10';

  if (loading || !settings) {
    return (
      <div className="container mx-auto p-6 max-w-5xl">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-6xl space-y-6">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-3xl font-bold text-white mb-2">Growth Incentives</h1>
        <p className="text-[#8A93A6]">Affiliate Program governance, Earn Free Month reviews, and payout rules.</p>
      </div>

      {/* Affiliate Program Governance */}
      <Card className="glass">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-blue-500" />
            Affiliate Program (Governance)
          </CardTitle>
          <CardDescription>Control program status, eligibility, and commission structure</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
            <div>
              <p className="text-white font-medium">Program Status</p>
              <p className="text-sm text-[#8A93A6]">Enable or pause affiliate onboarding and payouts</p>
            </div>
            <Switch
              checked={programSettings.enabled}
              onCheckedChange={(checked) => setProgramSettings({ ...programSettings, enabled: checked })}
              className={getToggleClass()}
            />
          </div>

          <div>
            <Label className="text-white">Eligibility Rules</Label>
            <Textarea
              className="mt-2 bg-[#1C1F26] border-white/10 text-white"
              value={programSettings.eligibility}
              onChange={(e) => setProgramSettings({ ...programSettings, eligibility: e.target.value })}
              placeholder="Describe who qualifies (e.g., active subscription, KYC verified)"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-white">Commission Type</Label>
              <select
                className="mt-2 w-full bg-[#1C1F26] border border-white/10 rounded-lg text-white px-3 py-2"
                value={programSettings.commissionType}
                onChange={(e) => setProgramSettings({ ...programSettings, commissionType: e.target.value })}
              >
                <option value="percent">Percent of invoice</option>
                <option value="fixed">Fixed amount</option>
              </select>
            </div>
            <div>
              <Label className="text-white">Commission Value</Label>
              <div className="mt-2 flex items-center gap-2">
                <Input
                  type="number"
                  className="bg-[#1C1F26] border-white/10 text-white"
                  value={programSettings.commissionValue}
                  onChange={(e) => setProgramSettings({ ...programSettings, commissionValue: parseFloat(e.target.value) || 0 })}
                />
                <Badge variant="outline" className="border-blue-500/30 text-blue-400 capitalize">
                  {programSettings.commissionType === 'percent' ? '%' : '$'}
                </Badge>
              </div>
            </div>
          </div>

          <div>
            <Label className="text-white">Commission Duration Cap (months)</Label>
            <Input
              type="number"
              className="mt-2 bg-[#1C1F26] border-white/10 text-white"
              value={programSettings.durationMonths}
              onChange={(e) => setProgramSettings({ ...programSettings, durationMonths: parseInt(e.target.value || '0', 10) })}
              min={1}
            />
            <p className="text-xs text-[#8A93A6] mt-1">Cap recurring commissions (e.g., 12 months).</p>
          </div>

          <Button onClick={saveProgram} className="w-full sm:w-auto">
            Save Affiliate Program
          </Button>
        </CardContent>
      </Card>

      {/* Earn Free Pro Month Reviews */}
      <Card className="glass">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5 text-green-500" />
            Earn Free Pro Month Reviews
          </CardTitle>
          <CardDescription>Manual approval incentive. Admin reviews each submission.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
            <div>
              <p className="text-white font-medium">Incentive Status</p>
              <p className="text-sm text-[#8A93A6]">Manual approval only. No auto-grant.</p>
            </div>
            <Switch
              checked={earnPro.enabled}
              onCheckedChange={(checked) => setEarnPro({ ...earnPro, enabled: checked })}
              className={getToggleClass()}
            />
          </div>

          <div>
            <Label className="text-white">Rules / Criteria</Label>
            <Textarea
              className="mt-2 bg-[#1C1F26] border-white/10 text-white"
              value={earnPro.rules}
              onChange={(e) => setEarnPro({ ...earnPro, rules: e.target.value })}
              placeholder="Outline the requirements for earning a free Pro month"
            />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-white font-medium">Pending Review Rewards</p>
              <div className="flex items-center gap-2 text-xs text-[#8A93A6]">
                <Badge variant="outline" className="border-yellow-500/30 text-yellow-400">Pending</Badge>
                <Badge variant="outline" className="border-green-500/30 text-green-400">Approved</Badge>
                <Badge variant="outline" className="border-red-500/30 text-red-400">Archived</Badge>
              </div>
            </div>

            {earnPro.submissions.length === 0 ? (
              <div className="text-center py-8 text-[#8A93A6] bg-white/5 border border-white/10 rounded-lg">
                No submissions yet
              </div>
            ) : (
              <div className="space-y-2">
                {earnPro.submissions.map((submission) => (
                  <div
                    key={submission.id}
                    className="p-3 rounded-lg bg-white/5 border border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                  >
                    <div>
                      <p className="text-white font-medium">{submission.title}</p>
                      <p className="text-xs text-[#8A93A6]">Source: {submission.source} • {new Date(submission.submitted_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" onClick={() => updateSubmissionStatus(submission.id, 'approved')}>
                        Approve
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => updateSubmissionStatus(submission.id, 'draft')}>
                        Reject
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => updateSubmissionStatus(submission.id, 'archived')}>
                        Archive
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <Button onClick={saveEarnPro} className="w-full sm:w-auto">Save Earn Free Month</Button>
            <Button variant="outline" onClick={() => setEarnPro(defaultEarnPro)}>Reset</Button>
          </div>
        </CardContent>
      </Card>

      {/* Payout Rules & Thresholds */}
      <Card className="glass">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-blue-500" />
            Payout Rules & Thresholds
          </CardTitle>
          <CardDescription>
            Control automated affiliate payouts globally
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Master Kill Switch */}
          <div className="flex items-center justify-between p-4 rounded-lg bg-white/5 border border-white/10">
            <div className="flex-1">
              <Label htmlFor="payout-enabled" className="text-white font-semibold">
                Enable Automatic Payouts
              </Label>
              <p className="text-sm text-[#8A93A6] mt-1">
                Master switch for all affiliate payouts. When OFF, commissions are recorded but NOT paid.
              </p>
            </div>
            <Switch
              id="payout-enabled"
              checked={settings?.payout_enabled || false}
              onCheckedChange={(checked) => setSettings({ ...settings, payout_enabled: checked })}
              className={getToggleClass()}
            />
          </div>

          {/* Auto-payout on invoice.paid */}
          <div className="flex items-center justify-between p-4 rounded-lg bg-white/5 border border-white/10">
            <div className="flex-1">
              <Label htmlFor="auto-payout" className="text-white font-semibold">
                Auto-Execute Payouts
              </Label>
              <p className="text-sm text-[#8A93A6] mt-1">
                Automatically execute Stripe Transfers when invoice.paid webhook fires
              </p>
            </div>
            <Switch
              id="auto-payout"
              checked={settings?.auto_payout_on_invoice_paid || false}
              onCheckedChange={(checked) =>
                setSettings({ ...settings, auto_payout_on_invoice_paid: checked })
              }
              disabled={!settings?.payout_enabled}
              className={getToggleClass()}
            />
          </div>

          {/* Minimum Threshold */}
          <div className="p-4 rounded-lg bg-white/5 border border-white/10">
            <Label htmlFor="threshold" className="text-white font-semibold">
              Minimum Payout Threshold
            </Label>
            <p className="text-sm text-[#8A93A6] mt-1 mb-3">
              Minimum balance required before executing payout
            </p>
            <div className="flex items-center gap-2">
              <span className="text-white">$</span>
              <Input
                id="threshold"
                type="number"
                step="0.01"
                value={settings?.minimum_payout_threshold || 50}
                onChange={(e) =>
                  setSettings({ ...settings, minimum_payout_threshold: parseFloat(e.target.value) })
                }
                className="max-w-[150px] bg-[#1C1F26] border-white/10 text-white"
              />
            </div>
          </div>

          {/* Stats Grid */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-2">
              <Card className="glass">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm text-[#8A93A6]">Pending Payouts</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-white">
                    ${stats.total_pending.toFixed(2)}
                  </div>
                </CardContent>
              </Card>

              <Card className="glass">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm text-[#8A93A6]">Total Paid</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-500">
                    ${stats.total_paid.toFixed(2)}
                  </div>
                </CardContent>
              </Card>

              <Card className="glass">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm text-[#8A93A6]">This Month</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-white">
                    ${stats.this_month_total.toFixed(2)}
                  </div>
                </CardContent>
              </Card>

              <Card className="glass">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm text-[#8A93A6]">Active Affiliates</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-white">
                    {stats.total_affiliates}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* How It Works */}
          <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
            <h4 className="font-semibold text-white mb-2">How Automated Payouts Work:</h4>
            <ol className="space-y-2 text-sm text-[#8A93A6]">
              <li className="flex items-start gap-2">
                <span className="text-blue-400 mt-0.5">1.</span>
                <span>When a referred user's subscription payment succeeds (invoice.paid), a commission is calculated</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-400 mt-0.5">2.</span>
                <span>Commission is capped at 12 months per referred user</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-400 mt-0.5">3.</span>
                <span>If payout is enabled AND affiliate has connected Stripe account, transfer is executed</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-400 mt-0.5">4.</span>
                <span>If payout is disabled, commission is recorded as "pending" for manual review</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-400 mt-0.5">5.</span>
                <span>Refunds automatically reverse commissions</span>
              </li>
            </ol>
          </div>

          {/* Save Button */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button 
              onClick={handleSavePayouts}
              disabled={saving}
              className="flex-1"
            >
              {saving ? 'Saving...' : 'Save Payout Settings'}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/app/admin/commissions')}
              className="flex items-center gap-2"
            >
              View All Commissions <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
